<template>
  <v-dialog
    :value="visible"
    @input="onDialogInput"
    width="85%"
    max-width="1100"
    persistent
    content-class="wpm-dialog"
  >
    <v-card class="wpm-card">
      <!-- Header -->
      <div class="wpm-header">
        <div class="wpm-header-left">
          <div class="wpm-header-icon">
            <v-icon color="white" size="22">mdi-calendar-edit</v-icon>
          </div>
          <div>
            <h2 class="wpm-title">Ajustar Proyección Semanal</h2>
            <span class="wpm-subtitle">
              Requerimiento #{{ computedReqId }} — {{ reqQuantity }} vacantes
              por semana
            </span>
          </div>
          <div style="width: 100%; text-align: right">
            <v-chip
              v-if="currentPeriod"
              small
              outlined
              color="primary"
              class="wpm-current-chip font-weight-bold"
              style="float: right"
              :aria-label="`Semana actual: ${currentPeriod.PdsName}`"
            >
              <v-icon x-small left>mdi-calendar-today</v-icon>
              Semana Actual: {{ currentPeriod.PdsName }}
            </v-chip>
          </div>
        </div>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <!-- Toolbar -->
      <div class="wpm-toolbar">
        <div class="wpm-toolbar-left">
          <div class="wpm-info-group">
            <v-alert
              type="info"
              outlined
              dense
              class="wpm-info-alert mb-0"
              role="note"
            >
              <strong>Regla:</strong> Semanas bloqueadas desde el Jueves (00:00)
              anterior.
            </v-alert>
          </div>
        </div>

        <div class="wpm-toolbar-right">
          <v-select
            v-model="selectedFilter"
            :items="filterOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-filter-variant"
            class="wpm-filter-select"
            aria-label="Filtro de visualización de semanas"
          />

          <v-btn
            color="primary"
            small
            class="wpm-btn"
            :loading="addWeekLoading"
            @click="openAddWeek"
          >
            <v-icon small class="mr-1">mdi-plus</v-icon>
            Agregar Semana
          </v-btn>

          <v-btn
            outlined
            color="secondary"
            small
            class="wpm-btn"
            @click="showHistory = true"
          >
            <v-icon small class="mr-1">mdi-history</v-icon>
            Ver Historial
          </v-btn>
        </div>
      </div>

      <v-divider />

      <!-- Body -->
      <div class="wpm-body">
        <div v-if="loading" class="wpm-loading">
          <v-progress-circular indeterminate color="primary" />
          <span>Cargando proyecciones...</span>
        </div>

        <projection-table
          v-else
          :week-groups="displayedWeekGroups"
          :total-registered-count="allWeekGroups.length"
          :req-quantity="reqQuantity"
          :selected-filter="selectedFilter"
          @edit-week="openEditWeek"
          @show-all="selectedFilter = 'all'"
        />
      </div>

      <v-divider />

      <!-- Footer -->
      <div class="wpm-footer">
        <v-btn
          outlined
          color="grey darken-1"
          class="wpm-btn"
          @click="closeDialog"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          class="wpm-btn wpm-btn--save"
          :loading="saving"
          :disabled="!hasChanges || saving"
          @click="saveAllChanges"
        >
          <v-icon small class="mr-1">mdi-content-save-outline</v-icon>
          Guardar Todos los Ajustes
        </v-btn>
      </div>
    </v-card>

    <!-- WeekFormModal child -->
    <week-form-modal
      :visible.sync="showWeekForm"
      :mode="weekFormMode"
      :week-data="weekFormData"
      :week-name="weekFormName"
      :req-quantity="reqQuantity"
      :period-year="periodYear"
      :periods-list="effectivePeriodsList"
      :req-id="computedReqId"
      :reqID="computedReqId"
      @on-save="onWeekFormSaved"
      @close="showWeekForm = false"
    />

    <!-- HistoryDrawer child -->
    <history-drawer
      :visible.sync="showHistory"
      :history-list="fullHistoryList"
      :req-quantity="reqQuantity"
      @close="showHistory = false"
    />
  </v-dialog>
</template>

<script>
import ProjectionTable from "./ProjectionTable.vue";
import WeekFormModal from "./WeekFormModal.vue";
import HistoryDrawer from "./HistoryDrawer.vue";
import _sHmnStaffingRequirement from "@/services/HumanResource/RecruitmentSelection/HmnStaffingRequirement.js";
import _sCreationPeriods from "@/services/HumanResource/CreationPeriods.js";

export default {
  name: "WeeklyProjectionManager",
  components: {
    ProjectionTable,
    WeekFormModal,
    HistoryDrawer,
  },
  props: {
    visible: { type: Boolean, default: false },
    reqId: { type: [Number, String], default: 0 },
    reqID: { type: [Number, String], default: 0 },
    reqQuantity: { type: Number, default: 0 },
    periodsList: { type: Array, default: () => [] },
    periodYear: { type: Number, default: () => new Date().getFullYear() },
  },
  data() {
    return {
      loading: false,
      saving: false,
      adjustmentRows: [],
      fullHistoryList: [],
      originalRows: [],
      internalPeriodsList: [],
      selectedFilter: "default",
      showWeekForm: false,
      weekFormMode: "edit",
      weekFormData: {},
      weekFormName: "",
      showHistory: false,
      changesMade: false,
    };
  },
  computed: {
    computedReqId() {
      return Number(this.reqId || this.reqID) || 0;
    },
    effectivePeriodsList() {
      return this.periodsList && this.periodsList.length
        ? this.periodsList
        : this.internalPeriodsList;
    },
    currentPeriod() {
      if (!this.effectivePeriodsList || !this.effectivePeriodsList.length) {
        return null;
      }
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const todayStr = `${year}-${month}-${day}`;

      return (
        this.effectivePeriodsList.find((p) => {
          if (!p.PdsDateBegin || !p.PdsDateEnd) return false;
          const b = String(p.PdsDateBegin).slice(0, 10);
          const e = String(p.PdsDateEnd).slice(0, 10);
          return b <= todayStr && e >= todayStr;
        }) || null
      );
    },
    nextPeriod() {
      if (!this.currentPeriod || !this.effectivePeriodsList.length) return null;
      const currNum = Number(this.currentPeriod.PdsNum);
      return (
        this.effectivePeriodsList.find(
          (p) => Number(p.PdsNum) === currNum + 1,
        ) || null
      );
    },
    allWeekGroups() {
      const groups = {};
      const activeRows = this.adjustmentRows.filter(
        (r) => r.SecStatus !== 0 && r.PdsID,
      );

      activeRows.forEach((r) => {
        const key = r.PdsID;
        if (!groups[key]) {
          const periodInfo =
            this.effectivePeriodsList.find(
              (p) => String(p.PdsID) === String(key),
            ) || {};
          const isLocked = this.isWeekLocked(
            periodInfo,
            periodInfo.PdsDateBegin || r.PdsDateBegin,
          );
          groups[key] = {
            PdsID: key,
            PdsNum:
              periodInfo.PdsNum !== undefined
                ? Number(periodInfo.PdsNum)
                : null,
            weekName: periodInfo.PdsName || `Semana ${key}`,
            PdsDateBegin: periodInfo.PdsDateBegin || r.PdsDateBegin,
            PdsDateEnd: periodInfo.PdsDateEnd,
            isLocked: isLocked,
            turns: [],
            totalQty: 0,
            totalMale: 0,
            totalFemale: 0,
          };
        }
        groups[key].turns.push(r);
        groups[key].totalQty += Number(r.WprQuantityProject) || 0;
        groups[key].totalMale += Number(r.WprQuantityMale) || 0;
        groups[key].totalFemale += Number(r.WprQuantityFemale) || 0;
      });

      // Sort by PdsNum or PdsID
      return Object.values(groups).sort((a, b) => {
        if (a.PdsNum !== null && b.PdsNum !== null) {
          return a.PdsNum - b.PdsNum;
        }
        return (a.PdsID || 0) - (b.PdsID || 0);
      });
    },
    displayedWeekGroups() {
      const all = this.allWeekGroups;
      if (!all || all.length === 0) return [];

      if (this.selectedFilter === "all") {
        return all;
      }

      if (
        this.selectedFilter !== "default" &&
        this.selectedFilter !== null &&
        this.selectedFilter !== undefined
      ) {
        // Filter by specific PdsID
        const filtered = all.filter(
          (w) => String(w.PdsID) === String(this.selectedFilter),
        );
        return filtered.length > 0 ? filtered : all;
      }

      // ── MODO POR DEFECTO: Máximo 2 semanas (1 Inactiva y 1 Activa) ──
      // 1. Identificar la semana inactiva (la última / más reciente entre las inactivas registradas)
      const inactiveList = all.filter((w) => w.isLocked);
      const inactiveWeek =
        inactiveList.length > 0 ? inactiveList[inactiveList.length - 1] : null;

      // 2. Identificar la semana activa (la primera / más próxima entre las activas registradas)
      const activeList = all.filter((w) => !w.isLocked);
      const activeWeek = activeList.length > 0 ? activeList[0] : null;

      const result = [];
      if (inactiveWeek) result.push(inactiveWeek);
      if (
        activeWeek &&
        (!inactiveWeek ||
          String(activeWeek.PdsID) !== String(inactiveWeek.PdsID))
      ) {
        result.push(activeWeek);
      }

      // Si no hubo coincidencia mixta (ej. todas activas o todas inactivas), devolver máximo 2
      if (result.length === 0) {
        return all.slice(0, 2);
      }

      return result.sort((a, b) => {
        if (a.PdsNum !== null && b.PdsNum !== null) {
          return a.PdsNum - b.PdsNum;
        }
        return (a.PdsID || 0) - (b.PdsID || 0);
      });
    },
    filterOptions() {
      const options = [
        {
          value: "default",
          text: `Vista Predeterminada (Máx. 2 Semanas: Inactiva + Activa)`,
        },
        {
          value: "all",
          text: `Ver Todas las Semanas Registradas (${this.allWeekGroups.length})`,
        },
      ];

      if (this.allWeekGroups && this.allWeekGroups.length > 0) {
        this.allWeekGroups.forEach((w) => {
          options.push({
            value: w.PdsID,
            text: `${w.weekName} — ${
              w.isLocked ? "Inactiva (Congelada)" : "Activa (Abierta)"
            }`,
          });
        });
      }

      return options;
    },
    hasChanges() {
      return this.changesMade;
    },
  },
  watch: {
    visible(val) {
      if (val && this.computedReqId) {
        this.loadData();
      }
    },
    reqId(val) {
      if (val && this.visible) {
        this.loadData();
      }
    },
    reqID(val) {
      if (val && this.visible) {
        this.loadData();
      }
    },
  },
  created() {
    if (this.computedReqId) {
      this.loadData();
    }
  },
  methods: {
    // ── Data Loading ──────────────────────
    async loadData() {
      const id = this.computedReqId;
      if (!id) return;
      this.loading = true;
      try {
        // Ensure periods list is available
        if (!this.periodsList || this.periodsList.length === 0) {
          try {
            const pRes = await _sCreationPeriods.list(
              1,
              this.periodYear,
              this.$fun.getUserID(),
            );
            this.internalPeriodsList = pRes.data || [];
          } catch (e) {
            console.error("Error loading internal periods:", e);
          }
        }

        const response = await _sHmnStaffingRequirement.byID(
          id,
          this.$fun.getUserID(),
        );
        const projections = response.data.Projections || [];

        // Get original snapshot for immutability
        let originalProjections = [];
        if (response.data.ReqSnapshotJSON) {
          try {
            const snap = JSON.parse(response.data.ReqSnapshotJSON);
            originalProjections = snap.Projections || [];
          } catch (e) {
            console.error(e);
          }
        }

        this.adjustmentRows = projections.map((p) => {
          const periodInfo =
            this.effectivePeriodsList.find(
              (per) => String(per.PdsID) === String(p.PdsID),
            ) || {};
          const orig = originalProjections.find(
            (o) =>
              String(o.PdsID) === String(p.PdsID) &&
              Number(o.TypeTurn) === Number(p.TypeTurn),
          );
          return {
            ...p,
            PdsDateBegin: periodInfo.PdsDateBegin,
            WprOriginalQty: orig
              ? orig.WprQuantityProject !== undefined
                ? orig.WprQuantityProject
                : 0
              : 0,
          };
        });

        // Deep clone for original state tracking
        this.originalRows = JSON.parse(JSON.stringify(this.adjustmentRows));
        this.changesMade = false;

        // Load history
        this.loadProjectionHistory();
      } catch (err) {
        this.$fun.alert(
          err.message || "Error al cargar la proyección",
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    loadProjectionHistory() {
      const id = this.computedReqId;
      if (!id) return;
      _sHmnStaffingRequirement
        .projectionHistory(id, this.$fun.getUserID())
        .then((res) => {
          this.fullHistoryList = res.data || [];
        })
        .catch(() => {});
    },

    // ── Week Lock Logic ──────────────────
    isWeekLocked(periodInfo, fallbackDateBegin) {
      if (this.$fun.isAdmin()) return false;

      const dateBeginStr =
        (periodInfo && periodInfo.PdsDateBegin) || fallbackDateBegin;
      if (!dateBeginStr) return true;

      // Safe date parsing avoiding timezone shifts
      const datePart = String(dateBeginStr).slice(0, 10);
      const [year, month, day] = datePart.split("-").map(Number);
      // Construct Monday beginDate at 00:00:00 local time
      const beginDate = new Date(year, month - 1, day, 0, 0, 0, 0);

      // Cutoff is Thursday (00:00:00) of the previous week (4 days before Monday)
      const limitDate = new Date(beginDate.getTime() - 4 * 24 * 60 * 60 * 1000);
      const now = new Date();

      return now >= limitDate;
    },

    // ── Week Form Modal ──────────────────
    openEditWeek(weekData) {
      this.weekFormMode = "edit";
      this.weekFormData = {
        PdsID: weekData.PdsID,
        turns: weekData.turns.map((t) => ({ ...t })), // shallow clone
      };
      this.weekFormName = weekData.weekName;
      this.showWeekForm = true;
    },

    openAddWeek() {
      this.weekFormMode = "add";
      this.weekFormData = { PdsID: null, turns: [] };
      this.weekFormName = "";
      this.showWeekForm = true;
    },

    onWeekFormSaved(payload) {
      const pdsID = payload.pdsID;
      const turns = payload.turns || [];

      // Remove all existing rows for this PdsID from adjustmentRows
      this.adjustmentRows = this.adjustmentRows.filter(
        (r) => String(r.PdsID) !== String(pdsID),
      );

      // Add the updated/new turns
      turns.forEach((turn) => {
        this.adjustmentRows.push({
          WprID: turn.WprID || 0,
          ReqID: turn.ReqID || this.computedReqId,
          PdsID: turn.PdsID || pdsID,
          WprQuantityProject: Number(turn.WprQuantityProject) || 0,
          TypeTurn: turn.TypeTurn,
          WprQuantityMale: Number(turn.WprQuantityMale) || 0,
          WprQuantityFemale: Number(turn.WprQuantityFemale) || 0,
          SecStatus: turn.SecStatus !== undefined ? turn.SecStatus : 1,
          UsrCreateID: turn.UsrCreateID || this.$fun.getUserID(),
          PdsDateBegin: this.getPeriodDateBegin(turn.PdsID || pdsID),
          WprOriginalQty: turn.WprOriginalQty || 0,
        });
      });

      this.changesMade = true;
      this.showWeekForm = false;
    },

    getPeriodDateBegin(pdsID) {
      if (!pdsID) return null;
      const period = this.effectivePeriodsList.find(
        (p) => String(p.PdsID) === String(pdsID),
      );
      return period ? period.PdsDateBegin : null;
    },

    getPeriodName(pdsID) {
      if (!pdsID) return "";
      const period = this.effectivePeriodsList.find(
        (p) => String(p.PdsID) === String(pdsID),
      );
      return period ? period.PdsName : `Semana ${pdsID}`;
    },

    // ── Save All ─────────────────────────
    async saveAllChanges() {
      // Validate: no duplicate turns per week
      const activeRows = this.adjustmentRows.filter(
        (r) => r.SecStatus !== 0 && r.PdsID,
      );
      const weekTurnsMap = {};
      for (const r of activeRows) {
        const key = `${r.PdsID}-${r.TypeTurn}`;
        if (weekTurnsMap[key]) {
          const weekName = this.getPeriodName(r.PdsID);
          this.$fun.alert(
            `Error: No puede registrar duplicados del mismo turno para la semana ${weekName}.`,
            "warning",
          );
          return;
        }
        weekTurnsMap[key] = true;
      }

      // Validate: weekly totals don't exceed reqQuantity
      const weeklyTotals = {};
      activeRows.forEach((r) => {
        weeklyTotals[r.PdsID] =
          (weeklyTotals[r.PdsID] || 0) + (Number(r.WprQuantityProject) || 0);
      });
      for (const pdsID in weeklyTotals) {
        if (weeklyTotals[pdsID] > this.reqQuantity) {
          const weekName = this.getPeriodName(pdsID);
          this.$fun.alert(
            `Error: La cantidad total para ${weekName} (${weeklyTotals[pdsID]}) supera el límite de ${this.reqQuantity} vacantes.`,
            "warning",
          );
          return;
        }
      }

      this.saving = true;
      try {
        await _sHmnStaffingRequirement.saveAdjustedProjections(
          this.adjustmentRows,
          this.$fun.getUserID(),
        );
        this.$fun.alert(
          "Los ajustes de proyección se guardaron correctamente.",
          "success",
        );
        this.changesMade = false;
        this.$emit("saved");
        this.closeDialog();
      } catch (err) {
        this.$fun.alert(err.message || "Error al guardar los ajustes", "error");
      } finally {
        this.saving = false;
      }
    },

    // ── Dialog Control ───────────────────
    closeDialog() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },

    onDialogInput(val) {
      if (!val) this.closeDialog();
    },
  },
};
</script>

<style scoped>
/* ── Card ────────────────────────────── */
.wpm-card {
  border-radius: 14px !important;
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
}

/* ── Header ──────────────────────────── */
.wpm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  flex-shrink: 0;
}

.wpm-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.wpm-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b5bdb 0%, #5b7cf7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(59, 91, 219, 0.25);
}

.wpm-title {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.02em;
}

.wpm-subtitle {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 400;
}

/* ── Toolbar ─────────────────────────── */
.wpm-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 12px;
}

.wpm-toolbar-left {
  flex: 1;
  min-width: 260px;
}

.wpm-info-alert {
  font-size: 12px;
  border-radius: 8px;
}

.wpm-current-chip {
  font-size: 12px !important;
  border-radius: 8px;
  background-color: #eff6ff !important;
  color: #1e40af !important;
}

.wpm-toolbar-right {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.wpm-filter-wrapper {
  min-width: 280px;
}

.wpm-filter-select ::v-deep .v-input__control {
  min-height: 32px !important;
}

.wpm-filter-select ::v-deep .v-input__slot {
  min-height: 32px !important;
  padding: 0 8px !important;
  font-size: 12px !important;
  background-color: #ffffff;
  border-radius: 8px;
}

.wpm-filter-select ::v-deep .v-select__selection {
  font-size: 12px !important;
  font-weight: 600;
  color: #334155;
}

.wpm-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
  letter-spacing: 0.01em;
}

/* ── Body ────────────────────────────── */
.wpm-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  background: #f8fafc;
}

.wpm-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px;
  color: #64748b;
  font-size: 14px;
}

/* ── Footer ──────────────────────────── */
.wpm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0;
  background: #ffffff;
}

.wpm-btn--save {
  min-width: 200px;
}

@media (max-width: 600px) {
  .wpm-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .wpm-toolbar-left,
  .wpm-toolbar-right {
    width: 100%;
    min-width: 0;
  }

  .wpm-filter-wrapper {
    min-width: 0;
    width: 100%;
  }

  .wpm-btn {
    flex: 1 1 auto;
  }
}
</style>
